import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import PipelineTimeline from "@/components/PipelineTimeline";
import { api } from "@/lib/api";
import { commissionFor } from "@/lib/commissions";

const DocCard = ({ tier, doc, full, when, deal, k, onOpen, applicableRate, negotiated }) => {
  const d = deal.documents?.[k] || { status: "pending" };
  const colorByStatus = {
    pending: "border-line text-sub",
    drafted: "border-warn text-warn",
    sent: "border-warn text-warn",
    signed: "border-success text-success",
    lodged: "border-oxblood text-oxblood",
    executed: "border-success text-success",
  };
  const buyerCanOpen =
    d.pdf_available && ["signed", "lodged", "executed"].includes(d.status);
  return (
    <div className="bg-parchment border border-line p-6" data-testid={`doc-${k}`}>
      <div className="flex items-baseline justify-between">
        <div>
          <div className="overline">{tier}</div>
          <h3 className="h-serif text-2xl text-ink mt-1">{doc}</h3>
          <div className="text-xs font-mono text-sub uppercase tracking-wider">{full}</div>
        </div>
        <span className={`text-[10px] font-mono uppercase tracking-wider border px-2 py-1 ${colorByStatus[d.status] || "border-line text-sub"}`}>
          {d.status}
        </span>
      </div>
      <p className="text-sub text-sm mt-4 leading-relaxed">{when}</p>
      {applicableRate && (
        <div className="mt-4 border-l-2 border-oxblood bg-parchment2 px-3 py-3"
             data-testid={`doc-rate-${k}`}>
          <div className="overline">Schedule range</div>
          <div className="font-mono text-xs text-ink mt-1 tabular">
            {applicableRate.min} <span className="text-sub">–</span> {applicableRate.max}
          </div>
          {applicableRate.barrel_note && (
            <div className="font-mono text-[10px] text-sub mt-0.5">{applicableRate.barrel_note}</div>
          )}
          {negotiated && negotiated.rate_value !== undefined && negotiated.rate_value !== null && (
            <div className="mt-3 pt-3 border-t border-line" data-testid={`doc-negotiated-${k}`}>
              <div className="overline !text-oxblood">Your negotiated rate</div>
              <div className="font-mono text-sm text-oxblood mt-1 tabular font-semibold">
                {Number(negotiated.rate_value).toLocaleString()} {negotiated.rate_unit}
              </div>
              {negotiated.total_estimate_usd ? (
                <div className="font-mono text-[10px] text-sub mt-1 tabular">
                  Est. total commission · ${Number(negotiated.total_estimate_usd).toLocaleString()}
                  {negotiated.lc_type ? ` · settled via ${negotiated.lc_type}` : ""}
                </div>
              ) : (
                negotiated.lc_type && (
                  <div className="font-mono text-[10px] text-sub mt-1">Settled via {negotiated.lc_type}</div>
                )
              )}
            </div>
          )}
        </div>
      )}
      {d.note && <p className="text-xs text-sub mt-3 font-mono italic">— {d.note}</p>}
      {d.updated_at && <p className="text-[10px] text-sub mt-3 font-mono">Updated: {d.updated_at?.slice(0, 19).replace("T", " ")}</p>}
      {buyerCanOpen ? (
        <button
          onClick={() => onOpen(k)}
          className="mt-4 text-[11px] font-mono uppercase px-3 py-2 border border-oxblood text-oxblood hover:bg-oxblood hover:text-parchment transition-colors w-full"
          data-testid={`buyer-open-${k}`}
        >
          Open executed copy ↗
        </button>
      ) : d.pdf_available ? (
        <p className="text-[10px] font-mono text-sub mt-4 italic">
          Draft on file with the brokerage desk. Released once signed.
        </p>
      ) : null}
    </div>
  );
};

export default function DealDetail() {
  const { id } = useParams();
  const [deal, setDeal] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/deals/${id}`).then((r) => setDeal(r.data)).catch(() => setError("Deal not found"));
  }, [id]);

  const openDoc = async (doc) => {
    try {
      const res = await api.get(`/deals/${id}/documents/${doc}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      window.console.error("Open failed", e);
    }
  };

  if (error) {
    return (
      <DashboardLayout>
        <div className="p-12 text-danger">{error}</div>
      </DashboardLayout>
    );
  }
  if (!deal) {
    return (
      <DashboardLayout>
        <div className="p-12 overline">Loading deal…</div>
      </DashboardLayout>
    );
  }

  const rate = commissionFor(deal.commodity);

  return (
    <DashboardLayout testid="deal-detail-page">
      <div className="p-8 md:p-12 max-w-6xl">
        <Link to="/dashboard/deals" className="link text-sub text-xs" data-testid="back-to-deals">← Back to deals</Link>
        <div className="overline mt-4">{deal.deal_id}</div>
        <h1 className="h-serif text-5xl text-ink mt-2">{deal.commodity}</h1>
        <div className="font-mono text-xs text-sub mt-2 tabular">
          {Number(deal.volume).toLocaleString()} {deal.unit} · ${Number(deal.target_price).toLocaleString()}/{deal.unit} · {deal.incoterm} · {deal.delivery_port}
        </div>

        <div className="mt-10">
          <div className="overline">Pipeline</div>
          <PipelineTimeline stage={deal.stage} />
        </div>

        <section className="mt-12">
          <div className="overline">Legal hierarchy</div>
          <h2 className="h-serif text-3xl text-ink mt-1">Document status</h2>
          <div className="grid md:grid-cols-3 gap-5 mt-6">
            <DocCard tier="Tier 1" doc="NCNDA" k="ncnda" deal={deal} onOpen={openDoc}
                     full="Non-Circumvention, Non-Disclosure" when="Signed before any supplier identity is revealed. Binds buyer, seller and any co-brokers for 3–5 years." />
            <DocCard tier="Tier 2" doc="IMFPA" k="imfpa" deal={deal} onOpen={openDoc}
                     applicableRate={rate} negotiated={deal.commission}
                     full="Irrevocable Master Fee Protection" when="Lodged with the buyer's and seller's banks. Specifies the commission split routed off the top of the LC." />
            <DocCard tier="Tier 3" doc="SPA" k="spa" deal={deal} onOpen={openDoc}
                     full="Sales & Purchase Agreement + LC" when="Final contract. Names us as Facilitator. Releases only when the NCNDA and IMFPA are bank-lodged." />
          </div>
        </section>

        <section className="mt-12 grid md:grid-cols-2 gap-6">
          <div className="border border-line bg-parchment p-6" data-testid="deal-icpo-block">
            <div className="overline">ICPO &amp; BCL</div>
            <h3 className="h-serif text-2xl text-ink mt-1">Intake documents</h3>
            <dl className="mt-5 text-sm divide-y divide-line">
              <Row k="ICPO reference" v={deal.icpo_reference} />
              <Row k="ICPO issued" v={deal.icpo_issue_date || "—"} />
              <Row k="BCL reference" v={deal.bcl_reference} />
              <Row k="BCL bank" v={deal.bcl_issuing_bank} />
              {deal.bcl_swift && <Row k="SWIFT / BIC" v={deal.bcl_swift} mono />}
            </dl>
          </div>

          <div className="border border-line bg-parchment p-6" data-testid="deal-fco-block">
            <div className="overline">Seller&apos;s response</div>
            <h3 className="h-serif text-2xl text-ink mt-1">FCO — Full Corporate Offer</h3>
            {!deal.fco ? (
              <div className="mt-5 text-sub text-sm">
                No FCO recorded yet. The trade desk is sounding sellers. You will be
                notified once an offer that matches your target lands on the desk.
              </div>
            ) : (
              <dl className="mt-5 text-sm divide-y divide-line">
                <Row k="Pricing" v={`$${Number(deal.fco.pricing_per_unit).toLocaleString()} / ${deal.unit}`} />
                <Row k="Allocation capacity" v={`${Number(deal.fco.allocation_capacity).toLocaleString()} ${deal.unit}`} />
                <Row k="Offer valid until" v={deal.fco.validity_until || "—"} />
                {deal.fco.notes && <Row k="Desk notes" v={deal.fco.notes} />}
              </dl>
            )}
            <p className="text-[10px] font-mono text-sub mt-4 italic">
              Seller origin held in confidence by the brokerage until NCNDA executes.
            </p>
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
}

function Row({ k, v, mono }) {
  return (
    <div className="py-2.5 flex items-baseline justify-between">
      <span className="text-sub">{k}</span>
      <span className={`text-ink ${mono ? "font-mono text-xs" : ""}`}>{v}</span>
    </div>
  );
}
